import type { ReactNode } from "react";
import { useState } from "react";
import { useAuth } from "@/lib/auth/AuthProvider";
import { Loader2 } from "lucide-react";
import { useMasterProductPlan } from "./hooks";
import { MasterProductIntake } from "./MasterProductIntake";
import type { IntakePlan, IntakeScope } from "./types";

interface GateProps {
  scope: IntakeScope;
  renderLegacy: (managedOrderIds: string[]) => ReactNode;
  onCollected?: (plan: IntakePlan) => ReactNode;
}

export function IntakeModelGate(props: GateProps) {
  const { session } = useAuth();
  // A new account or product scope must never reuse the previous form state.
  return (
    <OwnedIntakeModelGate
      key={`${session?.user.id ?? "signed-out"}:${JSON.stringify(props.scope)}`}
      {...props}
    />
  );
}

function OwnedIntakeModelGate({ scope, renderLegacy, onCollected }: GateProps) {
  const query = useMasterProductPlan(scope);
  const [completed, setCompleted] = useState<IntakePlan | null>(null);
  const plan = completed ?? query.data;
  if (query.isLoading)
    return (
      <div className="px-15 py-30 flex items-center gap-10 type-body-md text-ink-muted">
        <Loader2 size={16} className="animate-spin" />
        Loading your health history…
      </div>
    );
  // A failed background refresh cannot revoke an explicit legacy decision or
  // discard an in-progress legacy form. A successful later mode change still
  // takes effect below; the first lookup remains closed until it succeeds.
  if (!plan || (query.error && plan.mode !== "legacy"))
    return (
      <IntakeLoadError
        onRetry={() => {
          void query.refetch();
        }}
        busy={query.isFetching}
      />
    );
  if (plan.mode === "legacy") return renderLegacy([]);
  // A purchased lander intake may be complete without a portal draft/session.
  // Exclude those owned orders while continuing remaining ID/consent steps.
  if (
    plan.mode === "collected" &&
    plan.collection_complete &&
    plan.managed_order_ids?.length > 0
  )
    return renderLegacy(plan.managed_order_ids);
  if (plan.mode === "blocked")
    return (
      <div
        className="px-15 md:px-40 py-30 max-w-[64rem] mx-auto space-y-15"
        role="alert"
      >
        <h1 className="type-heading-md">Your intake needs a review</h1>
        <p className="type-body-md">
          {plan.message ||
            "This intake is being prepared. Your clinical team needs to resolve the form requirements before you can continue."}
        </p>
        <button
          type="button"
          className="underline type-body-md"
          onClick={() => {
            void query.refetch();
          }}
        >
          Check again
        </button>
      </div>
    );
  if (
    plan.mode !== "master_product" ||
    !plan.session_id ||
    plan.revision === undefined ||
    !Array.isArray(plan.components)
  )
    return (
      <IntakeLoadError
        onRetry={() => {
          void query.refetch();
        }}
        busy={query.isFetching}
      />
    );
  if (plan.collection_complete)
    return onCollected
      ? onCollected(plan)
      : renderLegacy(plan.managed_order_ids);
  return (
    <MasterProductIntake
      key={plan.session_id}
      initialPlan={plan}
      onCollected={setCompleted}
    />
  );
}

export function IntakeLoadError({
  onRetry,
  busy = false,
}: {
  onRetry: () => void;
  busy?: boolean;
}) {
  return (
    <div
      className="px-15 md:px-40 py-30 max-w-[64rem] mx-auto flex flex-col gap-15"
      role="alert"
    >
      <h1 className="type-heading-md">We couldn’t load your intake</h1>
      <p className="type-body-md">
        Your saved answers are safe. Please retry before continuing.
      </p>
      <button
        type="button"
        className="border border-line px-15 py-12 type-body-md"
        disabled={busy}
        onClick={onRetry}
      >
        {busy ? "Retrying…" : "Retry"}
      </button>
    </div>
  );
}
